import type { OperationSendOptions } from './WebSocketManager';
/**
 * WebSocket Singleton Manager
 *
 * This singleton ensures we have exactly ONE WebSocket connection
 * throughout the entire application lifecycle, preventing session ID
 * mismatches and multiple connection issues.
 */

import { WebSocketManager, type PrincipalContext, type DurableOperationOptions } from './WebSocketManager';
import { isNodeOperation } from './utils/queueManager';
import { log } from '@/shared/utils/logger';
import { OperationPromiseTracker } from './utils/operationPromiseTracker';
import { SessionIdManager } from './utils/sessionIdManager';
import { OperationDeduplicationTracker } from './utils/operationDeduplicationTracker';
import { HealthMonitor } from './utils/healthMonitor';
import { resolveOperationTransportMode } from './utils/operationTransportMode';

class WebSocketSingleton {
  private static instance: WebSocketSingleton;
  private manager: WebSocketManager | null = null;
  private sessionManager = new SessionIdManager();
  private isInitialized = false;
  private currentProjectId: string | null = null;
  private healthMonitor = new HealthMonitor();
  private currentUserId: string | null = null;
  private pendingConnectTimer: number | null = null;
  private pendingConnectOwner = 0;
  private deduplicationTracker = new OperationDeduplicationTracker();
  private operationTracker = new OperationPromiseTracker();
  private operationOwnerId: string | null = null;

  private constructor() {
    // Private constructor to prevent direct instantiation
  }

  public static getInstance(): WebSocketSingleton {
    if (!WebSocketSingleton.instance) {
      WebSocketSingleton.instance = new WebSocketSingleton();
    }
    return WebSocketSingleton.instance;
  }

  /**
   * Await a specific operation's server acknowledgement/result.
   */
  public waitForOperation(operationId: string, timeoutMs: number = 4000): Promise<unknown> {
    if (!operationId) return Promise.resolve(undefined);
    return this.operationTracker.waitForOperation(operationId, timeoutMs);
  }

  /**
   * Notify listeners that an operation received a result from server.
   */
  public notifyOperationResult(operationId: string, result: unknown): void {
    this.operationTracker.notifyOperationResult(operationId, result);
  }

  /**
   * Notify listeners of operation confirmation (alias to result for gating use-cases).
   */
  public notifyOperationConfirmed(operationId: string, result: unknown): void {
    this.operationTracker.notifyOperationConfirmed(operationId, result);
  }

  /**
   * Get or create a stable session ID that persists across component re-renders
   */
  public getStableSessionId(): string {
    return this.sessionManager.getStableSessionId();
  }

  /**
   * Initialize the WebSocket manager with configuration
   * This should only be called once during app initialization
   */
  public initialize(config: {
    url: string;
    onStateChange: (state: unknown) => void;
    onSyncStateChange: (state: unknown) => void;
    onMessage: (message: unknown) => void;
    onError: (error: unknown) => void;
    durableOperations?: DurableOperationOptions;
  }): WebSocketManager {
    if (this.isInitialized && this.manager) {
      log.debug('WebSocketSingleton', 'Manager already initialized, returning existing instance');
      this.manager.updateCallbacks({
        onStateChange: config.onStateChange,
        onSyncStateChange: config.onSyncStateChange,
        onMessage: config.onMessage,
        onError: config.onError,
      });
      return this.manager;
    }

    log.info('WebSocketSingleton', 'Initializing WebSocket manager');
    
    // Create the manager with the provided configuration
    this.manager = new WebSocketManager(config, config.durableOperations);
    this.isInitialized = true;
    
    return this.manager;
  }

  /**
   * Get the WebSocket manager instance
   * Note: Returns null if not initialized - caller should check before use
   */
  public getManager(): WebSocketManager | null {
    return this.manager;
  }

  /**
   * Connect to the WebSocket server
   * Uses the stable session ID to ensure consistency
   */
  public activatePrincipal(userId: string): PrincipalContext {
    if (this.operationOwnerId !== userId) {
      if (this.operationOwnerId !== null) this.disconnect();
      this.operationTracker.clearAll();
      this.deduplicationTracker.clear();
      this.operationOwnerId = userId;
    }
    this.currentUserId = userId;
    this.manager?.setUserContext(userId);
    return this.getPrincipalContext();
  }

  public getPrincipalContext(): PrincipalContext {
    return this.manager?.getPrincipalContext() ?? {userId: null, generation: -1};
  }

  public isCurrentPrincipal(owner: PrincipalContext): boolean {
    return this.manager?.isCurrentPrincipal(owner) ?? false;
  }

  public connect(userId: string, projectId: string = 'default'): (() => void) | undefined {
    if (!this.manager) {
      log.error('WebSocketSingleton', 'Cannot connect: Manager not initialized');
      return;
    }

    const previousProjectId = this.currentProjectId;
    this.activatePrincipal(userId);
    const currentState = this.manager.getState();

    this.currentUserId = userId;
    this.currentProjectId = projectId;

    // Check if already connected
    if (currentState === 'connected') {
      const sessionId = this.getStableSessionId();
      log.debug('WebSocketSingleton', 'Already connected with session:', sessionId);

      // Check if project changed - if so, switch subscription
      if (projectId && projectId !== previousProjectId) {
        log.info('WebSocketSingleton', 'Project changed from', previousProjectId, 'to', projectId, '- switching subscription');
        this.switchProject(projectId, previousProjectId || undefined);
      } else {
        log.debug('WebSocketSingleton', 'Same project, no subscription change needed');
      }
      return;
    }

    if (currentState === 'connecting') {
      log.debug('WebSocketSingleton', 'Connection already in progress, skipping duplicate connect');
      return;
    }

    const owner = ++this.pendingConnectOwner;
    const cancelPendingConnect = () => {
      if (owner !== this.pendingConnectOwner || this.pendingConnectTimer === null) return;
      window.clearTimeout(this.pendingConnectTimer);
      this.pendingConnectTimer = null;

    };

    if (this.pendingConnectTimer !== null) {
      log.debug(
        'WebSocketSingleton',
        'Connection already scheduled; preserving pending connect with the latest context',
        {
          userId: this.currentUserId,
          projectId: this.currentProjectId,
        }
      );
      return cancelPendingConnect;
    }

    this.pendingConnectTimer = window.setTimeout(() => {
      this.pendingConnectTimer = null;

      if (!this.manager || !this.currentUserId) {
        return;
      }

      const latestState = this.manager.getState();
      if (latestState === 'connected') {
        return;
      }

      if (latestState === 'connecting') {
        return;
      }

      const sessionId = this.sessionManager.generateFreshSessionId();
      log.info('WebSocketSingleton', 'Generated fresh session ID for new connection:', sessionId);
      log.debug('WebSocketSingleton', 'Connecting with:', {
        sessionId,
        userId: this.currentUserId,
        projectId: this.currentProjectId,
        currentState: latestState,
        timestamp: new Date().toISOString()
      });

      this.manager.connect(sessionId, this.currentUserId, this.currentProjectId || 'default');
      this.startHealthMonitoring();
    }, 75);
    return cancelPendingConnect;
  }

  /**
   * Maintain connection without unnecessary reconnections
   * Only connects if truly disconnected
   */
  public maintainConnection(userId: string, projectId: string, owner?: PrincipalContext): void {
    if (owner && !this.isCurrentPrincipal(owner)) throw new Error('Connection principal is obsolete');
    if (this.operationOwnerId !== userId) {
      this.connect(userId, projectId);
      return;
    }
    const currentState = this.getState();

    // Only connect if truly disconnected
    if (currentState === 'disconnected') {
      log.info('WebSocketSingleton', 'Connection lost, reconnecting with fresh session ID...');
      // Don't use cached session ID - connect() will generate a fresh one
      this.connect(userId, projectId);
    } else if (currentState === 'connected') {
      // Just ensure subscription without reconnecting
      log.debug('WebSocketSingleton', 'Connection active, ensuring subscription...');
      this.ensureProjectSubscription(projectId);
    }
    // If connecting, do nothing - let it complete
  }

  /**
   * Ensure project subscription without reconnecting
   */
  private ensureProjectSubscription(projectId: string): void {
    if (!this.manager) return;

    log.debug('WebSocketSingleton', 'Ensuring subscription to project:', projectId);
    this.manager.subscribeToProject(projectId);
  }

  /**
   * Switch to a different project while maintaining connection
   */
  public switchProject(projectId: string, oldProjectId?: string): void {
    if (!this.manager) {
      log.error('WebSocketSingleton', 'Cannot switch project: Manager not initialized');
      return;
    }

    if (!this.isConnected()) {
      log.error('WebSocketSingleton', 'Cannot switch project: Not connected');
      return;
    }

    const priorProjectId = oldProjectId ?? this.currentProjectId;
    log.info('WebSocketSingleton', 'Switching project from', priorProjectId, 'to', projectId);

    // Call the manager's switchProject method
    this.manager.switchProject(projectId, priorProjectId || undefined);
    this.currentProjectId = projectId;
  }

  /**
   * Start health monitoring for connection and subscriptions
   */
  private startHealthMonitoring(): void {
    this.healthMonitor.start({
      onHealthCheck: () => {
        // Check if we still have our project subscription
        if (this.currentProjectId && this.currentUserId) {
          // Send a lightweight ping to ensure subscription is active
          const pingMessage = {
            type: 'ping',
            projectId: this.currentProjectId,
            sessionId: this.getStableSessionId()
          };
          this.manager?.send(pingMessage);
        }
      },
      onConnectionLost: () => {
        if (this.currentUserId && this.currentProjectId) {
          log.debug('WebSocketSingleton', 'Connection lost during health check, attempting to restore connection');
          this.maintainConnection(this.currentUserId, this.currentProjectId);
        }
      },
      isConnected: () => this.isConnected()
    });
  }

  /**
   * Track an operation as processed to prevent double-application
   * CRITICAL: Now tracks with session context to allow proper multi-browser sync
   */
  public trackOperation(operationId: string, sessionId?: string): void {
    const effectiveSessionId = sessionId || this.sessionManager.getCurrentSessionId();
    if (!effectiveSessionId) {
      log.warn('WebSocketSingleton', 'No session ID available for tracking operation');
      return;
    }
    this.deduplicationTracker.trackOperation(operationId, effectiveSessionId);
  }

  /**
   * Check if an operation has already been processed by THIS session
   * CRITICAL: Now checks with session context to allow other browsers to process
   */
  public isOperationProcessed(operationId: string, sessionId?: string): boolean {
    const effectiveSessionId = sessionId || this.sessionManager.getCurrentSessionId();
    if (!effectiveSessionId) {
      log.warn('WebSocketSingleton', 'No session ID for checking operation');
      return false;
    }
    return this.deduplicationTracker.isOperationProcessed(operationId, effectiveSessionId);
  }

  /**
   * Clear processed operations (for cleanup)
   */
  public clearProcessedOperations(): void {
    this.deduplicationTracker.clear();
  }

  /**
   * Stop health monitoring
   */
  private stopHealthMonitoring(): void {
    this.healthMonitor.stop();
  }

  /**
   * Disconnect from the WebSocket server
   */
  public disconnect(): void {
    this.stopHealthMonitoring();

    if (this.pendingConnectTimer !== null) {
      clearTimeout(this.pendingConnectTimer);
      this.pendingConnectTimer = null;
    }

    if (this.manager) {
      log.info('WebSocketSingleton', 'Disconnecting WebSocket');
      this.manager.disconnect();
      this.currentProjectId = null;
      this.currentUserId = null;
    }
  }

  /**
   * Check if connected
   */
  public isConnected(): boolean {
    return this.manager?.isConnected() || false;
  }

  /**
   * Get current connection state
   */
  public getState(): string {
    return this.manager?.getState() || 'disconnected';
  }

  /**
   * Send operation through WebSocket
   */
  public async sendOperation(operation: Record<string, unknown>, projectId?: string, options: OperationSendOptions = {}): Promise<void> {
    if (!isNodeOperation(operation)) {
      throw new Error('Invalid operation; change was not admitted');
    }
    const manager = this.manager;
    const transportMode = resolveOperationTransportMode({
      hasManager: Boolean(manager),
      isConnected: this.isConnected() && !options.queueOnly,
    });

    if (transportMode === 'drop' || !manager) {
      throw new Error('Manager not initialized; change was not admitted');
    }

    if (transportMode === 'queue') {
      log.warn('WebSocketSingleton', 'Realtime channel is reconnecting; queuing operation until the socket is ready');
    }

    const owner = options.owner ?? this.getPrincipalContext();
    if (!this.isCurrentPrincipal(owner)) throw new Error('Operation principal is obsolete; operation was not queued or sent');
    return manager.sendOperation(operation, projectId, { ...options, owner });
  }

  public flushQueuedOperations(): void {
    this.manager?.flushQueuedOperations();
  }

  /**
   * Send raw message through WebSocket
   */
  public send(message: Record<string, unknown>): void | Promise<void> {
    if (message.type === 'operation') {
      if (!this.manager) return Promise.reject(new Error('Manager not initialized; change was not admitted'));
      return this.manager.send(message);
    }
    if (!this.manager) {
      log.error('WebSocketSingleton', 'Cannot send message: Manager not initialized');
      return;
    }

    if (!this.isConnected()) {
      log.error('WebSocketSingleton', 'Cannot send message: Not connected');
      return;
    }

    return this.manager.send(message);
  }

  /**
   * Clear session on logout
   */
  public clearSession(): void {
    log.info('WebSocketSingleton', 'Clearing session');
    this.stopHealthMonitoring();
    this.disconnect();
    this.sessionManager.clearSession();
    this.currentUserId = null;
    this.currentProjectId = null;
    this.deduplicationTracker.clear();
    this.operationTracker.clearAll();
    this.operationOwnerId = null;
    this.manager = null;
    this.isInitialized = false;
  }

  /**
   * Reset the singleton (mainly for testing)
   */
  public static reset(): void {
    if (WebSocketSingleton.instance) {
      WebSocketSingleton.instance.clearSession();
    }
    WebSocketSingleton.instance = null as unknown as WebSocketSingleton;
  }
}

// Export the singleton instance
export const webSocketSingleton = WebSocketSingleton.getInstance();

// Export the class for type definitions
export { WebSocketSingleton };
